import { useState, useEffect } from "react";
import { Badge } from "@shared/ui/badge";
import { Edit, Trash2, Eye } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@shared/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { useDeleteReason, useGetTransferTypes } from "@features/cx/settings/hooks";
import { ValueLabel } from "@features/ax/shared/ValueLabel";

interface TransferType {
  id: string;
  code: string;
  configurationCode: string;
  name: string;
  longDescription: string;
  extraData: {
    responseTime: number;
    priorityLevel: number;
    resolutionTime: number;
    requiredApproval: boolean;
    acknowledgementTime: number;
    priorityLevelDisplay: string;
    documentTypes?: string[];
    documentTypesDisplay?: string[];
  };
}

interface TransferTypesTableProps {
  onEditType: (type: TransferType) => void;
}

export function TransferTypesTable({ onEditType }: TransferTypesTableProps) {
  const deleteTransferType = useDeleteReason();
   const [currentPage, setCurrentPage] = useState(1);
   const [showDeleteDialog, setShowDeleteDialog] = useState(false);
   const [showDetailDialog, setShowDetailDialog] = useState(false);
   const [typeToDelete, setTypeToDelete] =
     useState<TransferType | null>(null);
   const [typeToDetail, setTypeToDetail] =
     useState<TransferType | null>(null);
   const [itemsPerPage] = useState(PAGE_SIZE);
   const { data: transferTypes } = useGetTransferTypes(currentPage, itemsPerPage);
  
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: PAGE_SIZE,
    startItem: 0,
    endItem: 0,
  });

  // Update pagination state when data changes
  useEffect(() => {
    if (transferTypes) {
      const totalItems = transferTypes.count || 0;
      const totalPages = Math.ceil(totalItems / itemsPerPage);
      const startItem = (currentPage - 1) * itemsPerPage + 1;
      const endItem = Math.min(currentPage * itemsPerPage, totalItems);

      setPagination({
        currentPage,
        totalPages,
        totalItems,
        itemsPerPage,
        startItem: totalItems > 0 ? startItem : 0,
        endItem: totalItems > 0 ? endItem : 0,
      });
    }
  }, [transferTypes, currentPage, itemsPerPage]);

  const handlePageChange = async (page: number) => {
    if (page !== currentPage && page >= 1 && page <= pagination.totalPages) {
      setCurrentPage(page);
    }
  };
  const handleDeleteType = (type: TransferType) => {
    setTypeToDelete(type);
    setShowDeleteDialog(true);
  };

  const onDetailType = (type: TransferType) => {
    setTypeToDetail(type);
    setShowDetailDialog(true);
  };

  const confirmDelete = () => {
    if (typeToDelete) {
      const payloadDelete = {
        id: typeToDelete.id,
      };
      deleteTransferType.mutateAsync(payloadDelete);
      setShowDeleteDialog(false);
      setTypeToDelete(null);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent": return "destructive";
      case "high": return "secondary";
      case "medium": return "outline";
      case "low": return "secondary";
      default: return "outline";
    }
  };

  const columns: TableColumn<TransferType>[] = [
     {
       key: "configurationCode",
       header: "Code",
       sortable: true,
       width: "w-24",
       render: (value: string) => (
         <span className="font-mono">{value}</span>
       ),
     },
     {
       key: "name",
       header: "Transfer Type",
       sortable: true,
       render: (value: string) => (
         <span className="font-medium">{value}</span>
       ),
     },
     {
       key: "longDescription",
       header: "Description",
       sortable: false,
       width: "w-64",
       render: (value: string) => (
         <span className="max-w-xs truncate block" title={value}>
           {value}
         </span>
       ),
     },
     {
       key: "acknowledgementTime",
       header: "Ack. Time (Hrs)",
       sortable: true,
       width: "w-32",
       render: (value: string, row: TransferType) => (
         <span className="font-medium">{row.extraData.acknowledgementTime}</span>
       ),
     },
     {
       key: "responseTime",
       header: "Response Time (Hrs)",
       sortable: true,
       width: "w-36",
       render: (value: string, row: TransferType) => (
         <span className="font-medium">{row.extraData.responseTime}</span>
       ),
     },
     {
       key: "resolutionTime",
       header: "Resolution Time (Hrs)",
       sortable: true,
       width: "w-40",
       render: (value: string, row: TransferType) => (
         <span className="font-medium">{row.extraData.resolutionTime}</span>
       ),
     },
     {
       key: "priority",
       header: "Priority",
       sortable: true,
       width: "w-28",
       render: (value: string, row: TransferType) => (
         <Badge variant={getPriorityColor(row.extraData.priorityLevelDisplay)}>
           {row.extraData.priorityLevelDisplay}
         </Badge>
       ),
     },
     {
       key: "requiredApproval",
       header: "Requires Approval",
       sortable: true,
       width: "w-36",
       render: (value: boolean, row: TransferType) => (
         <Badge variant={row.extraData.requiredApproval ? "default" : "secondary"}>
           {row.extraData.requiredApproval ? "Yes" : "No"}
         </Badge>
       ),
     },
   ];

  const actions = [
    {
      label: "Edit",
      onClick: (row: TransferType) => {
        onEditType(row);
      },
      icon: <Edit className="h-4 w-4 mr-2" />,
    },
    {
      label: "View",
      onClick: (row: TransferType) => {
        onDetailType(row);
      },
      icon: <Eye className="h-4 w-4 mr-2" />,
    },
    {
      label: "Delete",
      onClick: (row: TransferType) => {
        handleDeleteType(row);
      },
      icon: <Trash2 className="h-4 w-4 mr-2" />,
    },
  ];

  const detailFields = typeToDetail ? [
    { label: "Code", value: <span className="font-mono">{typeToDetail.configurationCode}</span> },
    { label: "Name", value: typeToDetail.name },
    { label: "Description", value: <span className="whitespace-pre-wrap">{typeToDetail.longDescription}</span>, fullWidth: true },
    { 
      label: "Priority", 
      value: (
        <Badge variant={getPriorityColor(typeToDetail.extraData.priorityLevelDisplay)}>
          {typeToDetail.extraData.priorityLevelDisplay}
        </Badge>
      )
    },
    { 
      label: "Requires Approval", 
      value: (
        <Badge variant={typeToDetail.extraData.requiredApproval ? "default" : "secondary"}>
          {typeToDetail.extraData.requiredApproval ? "Yes" : "No"}
        </Badge>
      )
    },
    { label: "Ack. Time", value: `${typeToDetail.extraData.acknowledgementTime} Hrs` },
    { label: "Response Time", value: `${typeToDetail.extraData.responseTime} Hrs` },
    { label: "Resolution Time", value: `${typeToDetail.extraData.resolutionTime} Hrs` },
  ] : [];

  return (
    <div className="space-y-4 mt-5">
      <GenericTable
        data={transferTypes?.results}
        columns={columns}
        rowKey="id"
        selectable={false}
        actions={actions}
        actionsType="dropdown"
        emptyMessage="No transfer types found"
        loading={false}
        pagination={pagination}
        onPageChange={handlePageChange}
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Transfer Type</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete \"{typeToDelete?.name}\"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Transfer Type Details</DialogTitle>
          </DialogHeader>
          {typeToDetail && (
            <div className="grid grid-cols-2 gap-6 py-4">
              {detailFields.map((field: any, idx) => (
                <div key={idx} className={field.fullWidth ? "col-span-2" : "col-span-1"}>
                  <ValueLabel label={field.label} value={field.value} />
                </div>
              ))}

              {typeToDetail.extraData.documentTypesDisplay && typeToDetail.extraData.documentTypesDisplay.length > 0 && (
                <div className="col-span-2 mt-4 space-y-2">
                  <p className="text-sm font-semibold border-b pb-2">Required Documents</p>
                  <div className="flex flex-wrap gap-2 pt-1">
                    {typeToDetail.extraData.documentTypesDisplay.map((doc, index) => (
                      <Badge key={index} variant="secondary" className="bg-blue-50 text-blue-700 border-blue-100">
                        {doc}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}